import React, { type ComponentProps } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import { theme } from '@garnish/constants';
import { useResponsive } from '@garnish/core';
import {
  CUSTOM_NAVIGATION_HEADER_HEIGHT,
  CUSTOM_NAVIGATION_HEADER_HORIZONTAL_PADDING,
  CustomNavigationHeader,
} from '@garnish/navigation';

// ─── Components ──────────────────────────────────────────────────────────────

const CustomizationNavigationHeaderContainer = (
  props: CustomizationSelectedIngredientsContainerProps,
) => {
  const { children, spacing = 'standard', ...restProps } = props;

  const { currentBreakpoint, match } = useResponsive();
  const {
    top: safeAreaTopInset,
    right: safeAreaRightInset,
    left: safeAreaLeftInset,
  } = useSafeAreaInsets();

  // ─── Derived ───────────────────────────────────────────────────────────────

  const rightInset = currentBreakpoint.isXS ? safeAreaRightInset : 0;
  const leftInset = currentBreakpoint.isXS ? safeAreaLeftInset : 0;
  const hasHorizontalInset = leftInset > 0 || rightInset > 0;

  // ─── Styles ──────────────────────────────────────────────────────────

  const containerDynamicStyles = useStyle(() => {
    const topOffset = Platform.select({
      android: safeAreaTopInset,
      default: 0,
    });
    const minHeight = Math.max(CUSTOM_NAVIGATION_HEADER_HEIGHT + topOffset, safeAreaTopInset);

    return {
      minHeight,
      marginTop: hasHorizontalInset ? theme.spacing['1'] : 0,
      marginRight: Math.max(rightInset - CUSTOM_NAVIGATION_HEADER_HORIZONTAL_PADDING, 0),
      marginLeft: Math.max(leftInset - CUSTOM_NAVIGATION_HEADER_HORIZONTAL_PADDING, 0),
    };
  }, [safeAreaTopInset, rightInset, leftInset, hasHorizontalInset]);

  const containerStyles = [
    styles.container,
    containerDynamicStyles,
    match([undefined, spacing === 'narrow' ? styles.outerContainerNarrowSM : undefined]),
  ];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={containerStyles}>
      <CustomNavigationHeader.Container
        palette="cream"
        safeAreaEdges={HEADER_SAFE_AREA_EDGES}
        gradientColors={HEADER_GRADIENT_COLORS}
        {...restProps}
      >
        {children}
      </CustomNavigationHeader.Container>
    </View>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const CustomizationNavigationHeaderContainerText = (
  props: CustomizationNavigationHeaderTextProps,
) => {
  const { children, ...restProps } = props;

  const { match } = useResponsive();

  return (
    <CustomNavigationHeader.Text
      textAlignment={match(['center', 'left'])}
      numberOfLines={1}
      style={styles.headerText}
      {...restProps}
    >
      {children}
    </CustomNavigationHeader.Text>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationNavigationHeader: Omit<
  typeof CustomNavigationHeader,
  'Container' | 'Text'
> & {
  Container: typeof CustomizationNavigationHeaderContainer;
  Text: typeof CustomizationNavigationHeaderContainerText;
} = {
  ...CustomNavigationHeader,
  Container: CustomizationNavigationHeaderContainer,
  Text: CustomizationNavigationHeaderContainerText,
};

// ─── Constants ───────────────────────────────────────────────────────────────

const HEADER_GRADIENT_COLORS = [theme.colors.CREAM, 'rgba(248, 247, 240, 0)'];
const HEADER_SAFE_AREA_EDGES: CustomizationSelectedIngredientsContainerProps['safeAreaEdges'] =
  Platform.select({ android: ['top'], default: [] });

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    justifyContent: 'center',
  },
  outerContainerNarrowSM: {
    paddingHorizontal: theme.spacing['6'],
  },
  headerText: {
    flex: 0,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type CustomizationSelectedIngredientsContainerProps = {
  spacing?: 'standard' | 'narrow';
} & ComponentProps<typeof CustomNavigationHeader.Container>;

type CustomizationNavigationHeaderTextProps = ComponentProps<typeof CustomNavigationHeader.Text>;
